import * as React from 'react';
import {StyleSheet, Text, TouchableOpacity, View} from 'react-native';
import {Field} from 'redux-form';
import {images} from '../../../../../../assets/icons';
import {useStyles} from '../../../../../hooks/styles';
import {useIsDesktop} from '../../../../../hooks/useIsDesktop';
import {LocalityResponsePayload} from '../../../../../models/api/localityResponsePayload';
import {useTheme} from '../../../../../theme';
import {DeviceSize} from '../../../../../theme/helpers';
import {BaseField} from '../../../../baseField';
import {LoadableContent} from '../../../../loadableContent';
import {ResponsiveView} from '../../../../responsiveView';
import {CustomScrollView} from '../../../../scrollView';

const cityListColumnCount = 3;
const cityListColumnWidth = 265;
const cityListColumnPaddingRight = 40;
const cityListItemShuffleToLeftPriority = 5;

interface CityListSpacer {
  isSpacer: boolean;
}

interface CityListHeading {
  isHeading: true;
  name: string;
}

type SortedCityList = Array<CityListHeading | CityListSpacer | LocalityResponsePayload>;

interface Props {
  list: LocalityResponsePayload[] | null;
  cityName: string;
  currentCityId: number;
  onClose?: () => void;
  onChange: (locality: LocalityResponsePayload) => void;
}

export default ({list, cityName, currentCityId, onChange}: Props) => {
  const isDesktop = useIsDesktop();
  const sortedList = React.useMemo<SortedCityList[]>(() => {
    const output: SortedCityList[] = [];
    if (!list || !list.length) {
      return output;
    }

    const mixedList: SortedCityList = [];
    list
      .slice()
      .sort((a, b) => ((a?.name ?? '') < (b?.name ?? '') ? -1 : 1))
      .forEach((listItem, index, orderedList) => {
        const hasRealName = typeof listItem.name === 'string' && listItem.name.length;
        if (!hasRealName) {
          return;
        }
        if (!index || orderedList[index - 1].name?.[0].toUpperCase() !== listItem.name?.[0].toUpperCase()) {
          mixedList.push({isSpacer: true}, {isHeading: true, name: listItem.name?.[0].toUpperCase()});
        }
        mixedList.push(listItem);
      });

    if (!isDesktop) {
      output.push(mixedList);
      return output;
    }

    const approximateColumnSize = Math.round(mixedList.length / cityListColumnCount);
    let currentColumnLeadingItemIndex = 0;
    Array(cityListColumnCount)
      .fill(true)
      .forEach((_, index) => {
        if (currentColumnLeadingItemIndex >= mixedList.length) {
          return;
        }
        if (index < cityListColumnCount - 1) {
          let nextColumnLeadingItemIndex = approximateColumnSize * (index + 1);
          if (!('isSpacer' in mixedList[nextColumnLeadingItemIndex])) {
            let stepsFromSectionStart = 0;
            let stepsToSectionEnd = 0;
            for (let i = nextColumnLeadingItemIndex - 1; i > 0; i--) {
              stepsFromSectionStart++;
              if ('isSpacer' in mixedList[i]) {
                break;
              }
            }
            for (let i = nextColumnLeadingItemIndex + 1; i < mixedList.length; i++) {
              stepsToSectionEnd++;
              if ('isSpacer' in mixedList[i]) {
                break;
              }
            }
            if (stepsFromSectionStart + cityListItemShuffleToLeftPriority < stepsToSectionEnd) {
              nextColumnLeadingItemIndex -= stepsFromSectionStart;
            } else {
              nextColumnLeadingItemIndex += stepsToSectionEnd;
              if (nextColumnLeadingItemIndex === mixedList.length - 1) {
                nextColumnLeadingItemIndex = mixedList.length;
              }
            }
          }
          output.push(mixedList.slice(currentColumnLeadingItemIndex, nextColumnLeadingItemIndex));
          currentColumnLeadingItemIndex = nextColumnLeadingItemIndex;
        } else {
          output.push(mixedList.slice(currentColumnLeadingItemIndex, mixedList.length));
        }
      });

    return output;
  }, [list, isDesktop]);

  const theme = useTheme();
  const styles = useStyles(({mediaOnly, platformOnly}) => ({
    title: {
      ...theme.fonts.h2,
      fontSize: 19,
      lineHeight: 29,
      color: theme.color.textColor,
      textAlign: 'center',
      marginBottom: 10,
    },
    selectedCityRow: {
      flexDirection: 'row',
      ...mediaOnly(DeviceSize.desktop, {justifyContent: 'center'}, {justifyContent: 'flex-start'}),
      alignItems: 'center',
    },
    selectedCityLabel: {
      ...theme.fonts.body,
      color: theme.color.textColor,
    },
    selectedCityValue: {
      ...theme.fonts.body,
      color: theme.color.primaryColor,
    },
    searchCityRow: {
      ...mediaOnly(DeviceSize.desktop, {marginTop: 40}, {marginTop: 20}),
      marginBottom: 30,
    },
    searchCityField: {
      width: '100%',
      color: theme.color.textColor,
      ...platformOnly('web', {borderColor: theme.color.primaryColor}),
      ...platformOnly('ios', {
        marginBottom: 12,
      }),
      ...platformOnly('android', {
        marginBottom: 8,
      }),
    },
    searchCityFieldInputContainer: {
      height: 40,
      maxHeight: 40,
      paddingBottom: 0,
    },
    searchCityFieldContainer: {
      height: 40,
      maxHeight: 40,
      justifyContent: 'flex-start',
    },
    searchCityFieldIconWrapper: {
      ...platformOnly('web', {}, {height: 40, minHeight: 40}),
    },
    cityListLoadableContainer: mediaOnly(
      DeviceSize.desktop,
      {
        minWidth: cityListColumnWidth * cityListColumnCount - cityListColumnPaddingRight,
      },
      {},
    ),
    cityListContainer: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      minHeight: 250,
    },
    cityListColumn: {
      flexDirection: 'column',
      justifyContent: 'flex-start',
      alignItems: 'flex-end',
    },
    cityListItem: {
      width: cityListColumnWidth,
      paddingVertical: 4,
      paddingRight: cityListColumnPaddingRight,
    },
    cityListSpacer: {
      height: 26,
    },
    cityListHeading: {
      ...mediaOnly(DeviceSize.desktop, theme.fonts.h2Light, theme.fonts.h6),
      color: theme.color.textColor,
    },
    cityListActiveItem: {},
    cityListActiveItemText: {
      ...theme.fonts.body,
      fontSize: 14,
      lineHeight: 18,
      color: theme.color.textColor,
    },
  }));

  const CityListContainer = isDesktop ? CustomScrollView : View;
  const citylistContainerProps = isDesktop
    ? {contentContainerStyle: styles.cityListContainer, dataSet: {class: 'custom-scroll'}}
    : {style: styles.cityListContainer};

  return (
    <>
      <ResponsiveView only={[DeviceSize.desktop]}>
        <Text testID='your-city-title-id' style={styles.title}>
          Ваш город
        </Text>
      </ResponsiveView>
      <View style={styles.selectedCityRow}>
        <Text testID='your-city-prevalue-id' style={styles.selectedCityLabel}>
          Выбран:{' '}
        </Text>
        <Text testID='your-city-value-id' style={styles.selectedCityValue}>
          {cityName}
        </Text>
      </View>
      <View style={styles.searchCityRow}>
        <Field
          component={BaseField}
          type='text'
          name='query'
          maxLength={30}
          placeholder='Поиск по городам'
          variant='outlined'
          smallHeight
          style={StyleSheet.flatten(styles.searchCityField) as any}
          inputContainerStyle={styles.searchCityFieldInputContainer}
          containerStyle={styles.searchCityFieldContainer}
          leftStyle={StyleSheet.flatten(styles.searchCityFieldIconWrapper)}
          left={<images.Search color={theme.color.nonActiveCheckBoxColor} width={18} height={18} />}
        />
      </View>
      <LoadableContent loading={!list} style={styles.cityListLoadableContainer}>
        <CityListContainer {...citylistContainerProps}>
          {sortedList.length ? (
            sortedList.map((column, columnIndex) => (
              <View key={columnIndex} style={styles.cityListColumn}>
                {column.map((item, itemIndex) => {
                  if ('isSpacer' in item && !itemIndex) {
                    return null;
                  }
                  const isLastColumn = columnIndex === cityListColumnCount - 1;
                  const isSelectedCity = 'id' in item && item?.id === currentCityId;
                  const stylesEx = isLastColumn
                    ? {paddingRight: 0, width: cityListColumnWidth - cityListColumnPaddingRight}
                    : undefined;

                  if ('isSpacer' in item) {
                    return <View key={itemIndex} style={[styles.cityListItem, styles.cityListSpacer, stylesEx]} />;
                  } else if ('isHeading' in item) {
                    return (
                      <Text key={itemIndex} style={[styles.cityListItem, styles.cityListHeading, stylesEx]}>
                        {item.name}
                      </Text>
                    );
                  } else {
                    return (
                      <TouchableOpacity
                        testID={`city-column-item-${itemIndex}-id`}
                        key={itemIndex}
                        style={[styles.cityListItem, styles.cityListActiveItem, stylesEx]}
                        onPress={() => onChange(item)}>
                        <Text
                          style={[styles.cityListActiveItemText, isSelectedCity && {color: theme.color.primaryColor}]}
                          numberOfLines={1}>
                          {item.name}
                        </Text>
                      </TouchableOpacity>
                    );
                  }
                })}
              </View>
            ))
          ) : (
            <Text style={[styles.cityListActiveItemText, {marginTop: 30}]}>Ничего не найдено</Text>
          )}
        </CityListContainer>
      </LoadableContent>
    </>
  );
};
